// Bill Pay & Recharge mock (spec §6, §9). Deterministic bills per biller+params,
// MPIN/OTP money movement via assertDebitAllowed, statement rows and
// notifications. Clock steps: settle pending, refund failed, run AutoPay.
import { loadDb, saveDb, type MockDb } from '../db';
import { mockNow, mockToday } from '../clock';
import {
  addNotification,
  addStatementRow,
  assertDebitAllowed,
  makeReference,
  registerClockStep,
} from '../phase4';
import type {
  BillFetch,
  BillPayment,
  BillComplaint,
  Mandate,
  SavedBiller,
} from '@/types/phase4.types';
import { billerById, autopayOtpLimitRupees, convenienceFeeRupees } from '../billPayCatalogue';

let seq = 0;
const nid = (p: string): string => `${p}_${Date.now().toString(36)}_${(seq++).toString(36)}`;

// ---------- deterministic bill (spec §6.3: ₹200–₹5,000, 1 in 5 overdue) ----------

const hash = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

export const fetchBill = (
  billerId: string,
  params: Record<string, string>,
): Promise<BillFetch> => {
  const db = loadDb();
  if (db.phase4.nextBillFails) {
    db.phase4.nextBillFails = false;
    saveDb(db);
    return Promise.reject(new Error("Couldn't fetch bill. Try again"));
  }
  const biller = billerById(billerId);
  if (!biller) return Promise.reject(new Error('Unknown biller.'));

  const seed = hash(billerId + Object.values(params).join('|'));
  const overdue = seed % 5 === 0;
  const amountRupees = 200 + (seed % 4801); // ₹200 .. ₹5,000
  const names = ['Aarav Shah', 'Diya Patel', 'Rohan Iyer', 'Meera Nair', 'Kabir Rao'];
  const customerName = names[seed % names.length];

  const base: BillFetch = { customerName, overdue };
  if (biller.mode === 'FETCH') {
    const today = mockToday(db);
    base.amountRupees = amountRupees;
    base.billDate = today;
    base.dueDate = overdue ? shift(today, -10) : shift(today, 12);
    if (overdue) base.info = 'Late fee may apply as per biller.';
  } else if (biller.mode === 'PLAN') {
    base.plans = [
      { id: 'p1', label: 'Popular', amountRupees: 299, detail: '2GB/day · 28 days' },
      { id: 'p2', label: 'Unlimited', amountRupees: 399, detail: '3GB/day · 28 days' },
      { id: 'p3', label: 'Data', amountRupees: 199, detail: '25GB · 30 days' },
      { id: 'p4', label: 'Talktime', amountRupees: 149, detail: '₹128 talktime · 24 days' },
      { id: 'p5', label: 'Validity', amountRupees: 99, detail: '28 days validity' },
    ];
  } else if (biller.mode === 'BOOKING') {
    base.amountRupees = 853;
    base.info = 'Booked · delivery in 2–3 days';
  } else if (biller.mode === 'VALIDATE') {
    base.info = biller.categoryId === 'fastag'
      ? 'Tag active · balance ₹1,240'
      : biller.categoryId === 'metro'
        ? 'Card balance ₹360'
        : 'Account validated';
  }
  return Promise.resolve(base);
};

const shift = (yyyymmdd: string, days: number): string => {
  const d = new Date(yyyymmdd + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ---------- pay (MPIN+OTP done by the caller via the auth chain) ----------

export const pay = (input: {
  billerId: string;
  params: Record<string, string>;
  amountRupees: number;
  nickname?: string;
  autopay?: boolean;
}): Promise<BillPayment> => {
  const db = loadDb();
  const biller = billerById(input.billerId);
  if (!biller) return Promise.reject(new Error('Unknown biller.'));

  const feeRupees = convenienceFeeRupees(input.billerId);
  const gate = assertDebitAllowed(db, input.amountRupees + feeRupees);
  if (!gate.ok) return Promise.reject(new Error(gate.message));

  let status: BillPayment['status'] = 'SUCCESS';
  if (db.phase4.nextBillOutcome === 'PENDING') status = 'PENDING';
  if (db.phase4.nextBillOutcome === 'FAILED') status = 'FAILED';
  db.phase4.nextBillOutcome = undefined;

  const payment: BillPayment = {
    id: nid('bp'),
    bcId: `BC${String(Math.floor(1e11 + Math.random() * 9e11))}`,
    billerId: input.billerId,
    billerName: biller.name,
    categoryId: biller.categoryId,
    params: input.params,
    amountRupees: input.amountRupees,
    feeRupees,
    status,
    autopay: input.autopay ?? false,
    at: mockNow(db),
  };
  db.phase4.billPayments.unshift(payment);

  if (status !== 'FAILED') {
    addStatementRow(db, {
      description: `${biller.name} bill`,
      amountRupees: -(input.amountRupees + feeRupees),
      category: 'bill',
      reference: payment.bcId,
    });
  }
  addNotification(
    db,
    status === 'SUCCESS' ? 'Bill paid' : status === 'PENDING' ? 'Bill payment pending' : 'Bill payment failed',
    status === 'FAILED'
      ? `${biller.name} payment of ₹${input.amountRupees} failed. Refund initiated.`
      : `${biller.name} ₹${input.amountRupees} — ${status === 'PENDING' ? 'Awaiting biller confirmation' : 'success'}.`,
  );

  // touch lastPaid on the matching saved biller
  const saved = db.phase4.billers.find((b) => b.billerId === input.billerId && sameParams(b.params, input.params));
  if (saved) saved.lastPaidAt = payment.at;
  saveDb(db);
  return Promise.resolve(payment);
};

const sameParams = (a: Record<string, string>, b: Record<string, string>): boolean =>
  Object.keys(a).length === Object.keys(b).length && Object.keys(a).every((k) => a[k] === b[k]);

export const payments = (): Promise<BillPayment[]> =>
  Promise.resolve(loadDb().phase4.billPayments);

// ---------- saved billers ----------

export const savedBillers = (): Promise<SavedBiller[]> => Promise.resolve(loadDb().phase4.billers);

export const saveBiller = (input: {
  billerId: string;
  params: Record<string, string>;
  nickname: string;
}): Promise<SavedBiller> => {
  const db = loadDb();
  const existing = db.phase4.billers.find((b) => b.billerId === input.billerId && sameParams(b.params, input.params));
  if (existing) {
    existing.nickname = input.nickname || existing.nickname;
    saveDb(db);
    return Promise.resolve(existing);
  }
  const biller: SavedBiller = {
    id: nid('sb'),
    billerId: input.billerId,
    nickname: input.nickname,
    params: input.params,
    favourite: false,
  };
  db.phase4.billers.unshift(biller);
  saveDb(db);
  return Promise.resolve(biller);
};

export const renameBiller = (id: string, nickname: string): Promise<void> => {
  const db = loadDb();
  const b = db.phase4.billers.find((x) => x.id === id);
  if (b) b.nickname = nickname;
  saveDb(db);
  return Promise.resolve();
};

export const deleteBiller = (id: string): Promise<void> => {
  const db = loadDb();
  db.phase4.billers = db.phase4.billers.filter((x) => x.id !== id);
  db.phase4.mandates = db.phase4.mandates.filter((m) => {
    const saved = db.phase4.billers.find((s) => s.id === m.billerId);
    return saved !== undefined;
  });
  saveDb(db);
  return Promise.resolve();
};

// ---------- AutoPay mandates (§6.4) ----------

export const mandates = (): Promise<Mandate[]> => Promise.resolve(loadDb().phase4.mandates);

export const createMandate = (input: Omit<Mandate, 'id' | 'paused' | 'skipNext' | 'createdAt'>): Promise<Mandate> => {
  const db = loadDb();
  if (db.phase4.mandates.some((m) => m.billerId === input.billerId)) {
    return Promise.reject(new Error('AutoPay is already set for this biller.'));
  }
  const mandate: Mandate = { ...input, id: nid('md'), paused: false, skipNext: false, createdAt: mockNow(db) };
  db.phase4.mandates.push(mandate);
  addNotification(db, 'AutoPay set', `${input.nickname} will be paid automatically up to ₹${input.capRupees}.`);
  saveDb(db);
  return Promise.resolve(mandate);
};

export const updateMandate = (id: string, patch: Partial<Mandate>): Promise<void> => {
  const db = loadDb();
  const m = db.phase4.mandates.find((x) => x.id === id);
  if (m) Object.assign(m, patch);
  saveDb(db);
  return Promise.resolve();
};

export const setMandatePaused = (id: string, paused: boolean): Promise<void> => updateMandate(id, { paused });

export const deleteMandate = (id: string): Promise<void> => {
  const db = loadDb();
  db.phase4.mandates = db.phase4.mandates.filter((x) => x.id !== id);
  saveDb(db);
  return Promise.resolve();
};

export const skipNextDebit = (id: string): Promise<void> => updateMandate(id, { skipNext: true });

export const holdMandateDebit = (id: string): Promise<void> => {
  const db = loadDb();
  const mandate = db.phase4.mandates.find((x) => x.id === id);
  if (!mandate) return Promise.reject(new Error('AutoPay mandate not found.'));
  mandate.pendingApprovalId = nid('hold');
  saveDb(db);
  return Promise.resolve();
};

export const approveMandateDebit = (id: string): Promise<void> => {
  const db = loadDb();
  const m = db.phase4.mandates.find((x) => x.id === id);
  if (m?.pendingApprovalId) {
    m.pendingApprovalId = undefined;
    void runMandateDebit(db, m, true);
  }
  saveDb(db);
  return Promise.resolve();
};

// ---------- complaints (§6.6) ----------

export const complaints = (): Promise<BillComplaint[]> => Promise.resolve(loadDb().phase4.billComplaints);

export const raiseComplaint = (input: {
  kind: 'TRANSACTION' | 'SERVICE';
  target: string;
  reason: string;
  description: string;
}): Promise<BillComplaint> => {
  if (input.kind === 'SERVICE' && input.description.trim().length < 10) {
    return Promise.reject(new Error('Tell us a little more (10 characters or more).'));
  }
  const db = loadDb();
  const complaint: BillComplaint = {
    id: nid('bcc'),
    reference: makeReference('BCC'),
    ...input,
    status: 'REGISTERED',
    createdAt: mockNow(db),
  };
  db.phase4.billComplaints.unshift(complaint);
  addNotification(db, 'Complaint registered', `Reference ${complaint.reference}.`);
  saveDb(db);
  return Promise.resolve(complaint);
};

// ---------- clock step: settle pending, refund failed, run AutoPay ----------

const runMandateDebit = (db: MockDb, m: Mandate, approved: boolean): void => {
  const saved = db.phase4.billers.find((s) => s.id === m.billerId);
  const biller = saved && billerById(saved.billerId);
  if (!saved || !biller) return;
  const seed = hash(biller.id + Object.values(saved.params).join('|'));
  const billRupees = 200 + (seed % 4801);

  if (m.skipNext) {
    m.skipNext = false;
    addNotification(db, 'AutoPay skipped', `${m.nickname}: this debit was skipped as requested.`);
    return;
  }
  if (billRupees > m.capRupees) {
    addNotification(db, 'AutoPay skipped', `${m.nickname} bill ₹${billRupees} is above your AutoPay cap ₹${m.capRupees}. Pay it yourself.`);
    return;
  }
  const limit = autopayOtpLimitRupees(biller.categoryId);
  if (!approved && billRupees > limit) {
    m.pendingApprovalId = nid('mdp');
    addNotification(db, 'Approve AutoPay', `${m.nickname} ₹${billRupees} needs your approval. Approve with OTP.`);
    return;
  }
  void pay({
    billerId: biller.id,
    params: saved.params,
    amountRupees: billRupees,
    autopay: true,
  }).catch(() => undefined);
};

registerClockStep((db, now) => {
  // Complaint tracker: registered → assigned to biller → resolved.
  // The mock clock makes both support hand-offs deterministic and testable.
  for (const complaint of db.phase4.billComplaints) {
    const age = now - complaint.createdAt;
    if (complaint.status === 'REGISTERED' && age >= 24 * 60 * 60 * 1000) {
      complaint.status = 'ASSIGNED';
    } else if (complaint.status === 'ASSIGNED' && age >= 48 * 60 * 60 * 1000) {
      complaint.status = 'RESOLVED';
      complaint.resolution = complaint.kind === 'TRANSACTION'
        ? 'The payment complaint was reviewed and resolved by the biller.'
        : 'The service complaint was reviewed and resolved by the biller.';
    }
  }

  // settle pending, refund failed
  for (const p of db.phase4.billPayments) {
    if (p.status === 'PENDING') {
      p.status = 'SUCCESS';
      addNotification(db, 'Bill payment confirmed', `${p.billerName} ₹${p.amountRupees} confirmed.`, now);
    } else if (p.status === 'FAILED') {
      p.status = 'SUCCESS';
      addStatementRow(db, { description: `${p.billerName} refund`, amountRupees: p.amountRupees + p.feeRupees, category: 'bill', reference: p.bcId }, now);
      addNotification(db, 'Refund credited', `${p.billerName} ₹${p.amountRupees} refunded.`, now);
    }
  }
  // run mandates due (mock due date = 15th each month, spec §6.4)
  const day = new Date(now).getDate();
  if (day === 15) {
    for (const m of db.phase4.mandates) {
      if (!m.paused) runMandateDebit(db, m, false);
    }
  }
});
